import { z } from "zod";

// Customer texting (PulseM-style): who's coming with their photo and bio, on
// the way with a link, and a review ask afterwards. See docs/pulsem-audit.md.
// Every automatic text is OFF until the company turns it on, and the review
// ask must stay off until the Twilio A2P campaign lists review requests.

/** A customer's texting state: consent and opt-out (STOP). */
export const CustomerSmsSchema = z.object({
  consent: z.enum(["unknown", "yes", "no"]).default("unknown"),
  source: z.enum(["web_form", "phone", "in_person", "import"]).nullable().default(null),
  consentAt: z.number().nullable().default(null),
  consentByUid: z.string().nullable().default(null),
  /** Replied STOP (or Twilio refused: opted out). Cleared by START. */
  optedOutAt: z.number().nullable().default(null),
});
export type CustomerSms = z.infer<typeof CustomerSmsSchema>;

/** What the customer sees about a tech on the "who's coming" page. */
export const MemberProfileSchema = z.object({
  publicName: z.string().max(40).default(""),
  bio: z.string().max(280).default(""),
  yearsExperience: z.number().int().min(0).max(70).nullable().default(null),
  certifications: z.array(z.string().max(60)).max(10).default([]),
  /** Firebase Storage download URL (tokenized), shown on the public page. */
  photoUrl: z.string().max(1000).nullable().default(null),
  /** Office-set: shown as a badge; and the profile only goes public once approved. */
  backgroundChecked: z.boolean().default(false),
  approved: z.boolean().default(false),
  updatedAt: z.number().default(0),
});
export type MemberProfile = z.infer<typeof MemberProfileSchema>;

/** What a reminder can say; the office writes the words, these fill in per visit. */
export const REMINDER_FIELDS = ["firstName", "company", "when", "date", "time", "tech", "phone", "address"] as const;
export const DEFAULT_REMINDER_BODY =
  "Hi {{firstName}}, this is {{company}}. A reminder that your appointment is {{when}}. Reply here or call {{phone}} if anything changes.";

/**
 * The night-before appointment reminder. Each job type (a department, for a
 * company like CSG) can have its own words or none; a type not listed uses
 * the default words.
 */
export const ReminderSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** Company time, the evening before ("17:00"). */
  sendAt: z.string().regex(/^\d{2}:\d{2}$/).default("17:00"),
  /** The default words; blank = DEFAULT_REMINDER_BODY. */
  body: z.string().max(500).default(""),
  jobTypes: z.record(z.string(), z.object({ enabled: z.boolean().default(true), body: z.string().max(500).default("") })).default({}),
});
export type ReminderSettings = z.infer<typeof ReminderSettingsSchema>;

/** "{{firstName}}" fields filled in; a missing one leaves no hole ("Hi, this is ..."). */
export function fillReminder(body: string, fields: Record<string, string>): string {
  return body
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k: string) => fields[k] ?? "")
    .replace(/ ,/g, ",")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/** tenants/{t}/settings/texting: the company's customer-texting choices. */
export const TextingSettingsSchema = z.object({
  /** Text "who's coming" when a tech is assigned to a job within the next day and a half. */
  autoIntro: z.enum(["off", "on_assign"]).default("off"),
  reviewRequest: z
    .object({
      enabled: z.boolean().default(false),
      delayMinutes: z.number().int().min(0).max(24 * 60).default(30),
      /** Job types that ask for a review; empty = all. */
      jobTypes: z.array(z.string()).default([]),
    })
    .default({}),
  reminder: ReminderSettingsSchema.default({}),
  /**
   * Builder work: a job on a lot texts the job's customer (the builder) or, when the
   * lot has a homeowner phone, the homeowner. Covers who's coming, on the way,
   * reminders and review asks.
   */
  lotTextsTo: z.enum(["customer", "homeowner"]).default("customer"),
  /** Automatic texts only go out inside this window (company time); later ones wait. */
  sendWindow: z.object({ start: z.string().regex(/^\d{2}:\d{2}$/), end: z.string().regex(/^\d{2}:\d{2}$/) }).default({ start: "08:00", end: "20:00" }),
  timezone: z.string().max(60).default(""),
  companyPhone: z.string().max(40).default(""),
  googleReviewUrl: z.string().max(500).default(""),
  facebookUrl: z.string().max(500).default(""),
  updatedAt: z.number().default(0),
});
export type TextingSettings = z.infer<typeof TextingSettingsSchema>;

/** tenants/{t}/messages/{id}: every text in or out (office-readable). */
export const MessageLogSchema = z.object({
  direction: z.enum(["out", "in"]),
  kind: z.string(),
  jobId: z.string().nullable().default(null),
  customerId: z.string().nullable().default(null),
  phone: z.string(),
  body: z.string(),
  status: z.enum(["sent", "failed", "skipped", "received"]),
  /** Why it was skipped or failed (plain words), or the Twilio error code. */
  reason: z.string().default(""),
  sid: z.string().nullable().default(null),
  actorName: z.string().default(""),
  createdAt: z.number(),
});
export type MessageLog = z.infer<typeof MessageLogSchema>;

/** tenants/{t}/feedback/{token}: what a customer said after the job. */
export const FeedbackSchema = z.object({
  jobId: z.string(),
  customerId: z.string().nullable().default(null),
  techUids: z.array(z.string()).default([]),
  stars: z.number().int().min(1).max(5),
  comment: z.string().max(2000).default(""),
  googleClicked: z.boolean().default(false),
  resolvedAt: z.number().nullable().default(null),
  createdAt: z.number(),
});
export type Feedback = z.infer<typeof FeedbackSchema>;

/** tenants/{t}/outbox/{id}: an automatic text waiting for its time (delay, send window). */
export const OutboxSchema = z.object({
  kind: z.enum(["intro", "review", "reminder", "referral_ask"]),
  /** Empty for a referral ask (it's about the customer, not a job). */
  jobId: z.string(),
  customerId: z.string().optional(),
  /** A referral ask sent by a campaign (its words), else the standing ask. */
  campaignId: z.string().optional(),
  /** Reminders: the visit's local date this one is for (moved visits get a new one). */
  forDate: z.string().optional(),
  sendAt: z.number(),
  status: z.enum(["pending", "sent", "skipped", "failed"]),
  reason: z.string().default(""),
  createdAt: z.number(),
  doneAt: z.number().nullable().default(null),
});
export type Outbox = z.infer<typeof OutboxSchema>;

/** customerLinks/{token} (top level, server-only): a public page for one job. */
export const CustomerLinkSchema = z.object({
  tenantId: z.string(),
  jobId: z.string(),
  kind: z.enum(["tech", "review"]),
  expiresAt: z.number(),
  createdAt: z.number(),
});
export type CustomerLink = z.infer<typeof CustomerLinkSchema>;
